// OPカレンダー関連の定数定義

// 予約ステータス
export const BOOKING_STATUS = {
  RESERVED: 1,
  CANCELLED: 2,
}

export const BOOKING_STATUS_LABELS: Record<number, string> = {
  [BOOKING_STATUS.RESERVED]: '予約',
  [BOOKING_STATUS.CANCELLED]: 'キャンセル',
}

// <InputRadio>用のオプション
export const BOOKING_STATUS_OPTIONS = [
  {
    id: 'confirmed',
    label: BOOKING_STATUS_LABELS[BOOKING_STATUS.RESERVED],
    value: String(BOOKING_STATUS.RESERVED),
  },
  {
    id: 'canceled',
    label: BOOKING_STATUS_LABELS[BOOKING_STATUS.CANCELLED],
    value: String(BOOKING_STATUS.CANCELLED),
  },
]

// ステータスIDからラベルを取得するヘルパー関数
export const getBookingStatusLabel = (statusId: number | string): string => {
  const id = typeof statusId === 'string' ? parseInt(statusId, 10) : statusId
  return BOOKING_STATUS_LABELS[id] || ''
}

// 正式依頼キャンセルのキャンセル理由（バックエンドの TourGuide.cancel_reason enum のキーと対応）
export const CANCEL_REASON_AGENT_CAUSE = 'agent_cause'
export const CANCEL_REASON_AGENT_TOUR_CHANGE = 'agent_tour_change'
export const CANCEL_REASON_GUIDE_CAUSE = 'guide_cause'
export const CANCEL_REASON_NATURAL_DISASTER = 'natural_disaster'

// キャンセル料が発生しない理由（バックエンドの TourGuidePaymentService#no_cancellation_fee? と対応）
export const NO_CANCELLATION_FEE_REASONS = [
  CANCEL_REASON_AGENT_TOUR_CHANGE,
  CANCEL_REASON_GUIDE_CAUSE,
  CANCEL_REASON_NATURAL_DISASTER,
]

// <InputRadio>用のオプション。ラベルは Rails i18n のキーで解決する
export const CANCEL_REASON_OPTIONS = [
  {
    id: 'agent_cause',
    value: CANCEL_REASON_AGENT_CAUSE,
    labelKey: 'activerecord.attributes.tour_guide.cancel_reason.agent_cause',
  },
  {
    id: 'agent_tour_change',
    value: CANCEL_REASON_AGENT_TOUR_CHANGE,
    labelKey: 'activerecord.attributes.tour_guide.cancel_reason.agent_tour_change',
  },
  {
    id: 'guide_cause',
    value: CANCEL_REASON_GUIDE_CAUSE,
    labelKey: 'activerecord.attributes.tour_guide.cancel_reason.guide_cause',
  },
  {
    id: 'natural_disaster',
    value: CANCEL_REASON_NATURAL_DISASTER,
    labelKey: 'activerecord.attributes.tour_guide.cancel_reason.natural_disaster',
  },
]

// キャンセル理由別のデフォルト文面。[cancellation_fee] は replaceVariableForMail 適用後に
// モーダルで計算済みのキャンセル料へ置換する独自プレースホルダ
const CANCEL_MAIL_SUBJECT =
  '※キャンセル※【[tour_type]】正式ご依頼（[tour_date_full] [start_time]＠[tour_short_name] ）'
const TOUR_CHANGE_MAIL_SUBJECT =
  '※コース変更のご相談※【[tour_type]】正式ご依頼（[tour_date_full] [start_time]＠[tour_short_name] ）'

export const CANCEL_MAIL_TEMPLATES: { [key: string]: { subject: string; message: string } } = {
  [CANCEL_REASON_AGENT_CAUSE]: {
    subject: CANCEL_MAIL_SUBJECT,
    message: `[guide_name]様

いつもお世話になっております。
株式会社羅針盤です。

本件について、ゲストより予約キャンセルがございましたので、
お願いしていたこちら(正式ご依頼番号: [ref_number])のツアーをキャンセルとさせてください。
ご準備頂いていたのに大変申し訳ございません。

業務委託契約書に基づき、キャンセル料をお支払いさせて頂きます。
キャンセル料　[cancellation_fee]円


【参考】
-催行8日前：キャンセル料は発生いたしません。
-催行7日前~催行4日前まで : 基本業務委託料(税抜) × 10%
-催行3日前~催行1日前まで : 基本業務委託料(税抜) × 25%
-催行当日 : 基本業務委託料(税抜) × 100%
※算出金額に小数点以下の端数が発生した場合、円未満は切り捨てとする。
※催行ツアー別手数料及び各種手当は、キャンセル料の対象外とする。


ご不明な点がございましたらお問い合わせくださいませ。
今後ともよろしくお願いいたします。

株式会社羅針盤`,
  },
  [CANCEL_REASON_AGENT_TOUR_CHANGE]: {
    subject: TOUR_CHANGE_MAIL_SUBJECT,
    message: `[guide_name]様

いつもお世話になっております。
株式会社羅針盤です。

表題のツアーにつきましてご相談です。
以下のツアーのガイド様を探しておりまして、
もしご都合等がよろしければ、コース変更いただくことは可能でしょうか？

■ツアー詳細




ご検討いただけますと幸いです。
何卒よろしくお願いいたします。

株式会社羅針盤

(正式ご依頼番号: [ref_number])`,
  },
  [CANCEL_REASON_GUIDE_CAUSE]: {
    subject: CANCEL_MAIL_SUBJECT,
    message: `[guide_name]様

いつもお世話になっております。
株式会社羅針盤です。

本件について、ガイド様のお申し出によりキャンセルとさせていただきます。
恐れ入りますが、ガイド様のご都合によるキャンセルのため、キャンセル料は発生いたしません。
ご了承くださいますようお願い申し上げます。

ご不明な点がございましたらお問い合わせくださいませ。
今後ともよろしくお願いいたします。

株式会社羅針盤

(正式ご依頼番号: [ref_number])`,
  },
  [CANCEL_REASON_NATURAL_DISASTER]: {
    subject: CANCEL_MAIL_SUBJECT,
    message: `[guide_name]様

いつもお世話になっております。
株式会社羅針盤です。

本件について、天候の影響を鑑みて
お願いしていたこちらのツアー(正式ご依頼番号: [ref_number])をキャンセルとさせてください。
ご準備頂いていたのに大変申し訳ございません。

恐れ入りますが、天変地異による催行中止のため、契約書/規約に基づき
キャンセル料は発生いたしません。

参考:
(3) 天変地異(気象警報発令時含む)又は感染症拡大予防等の甲及び乙の
いずれの責にも帰さない事由により本業務が催行不可となる場合、
甲乙間において、キャンセル料について協議できるものとする。

ご了承くださいますようお願い申し上げます。

ご不明な点がございましたらお問い合わせくださいませ。
今後ともよろしくお願いいたします。

株式会社羅針盤`,
  },
}
